import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';

import { sendMessageToBarry } from '~api/common/barry/barry';
import type { ApiError } from '~types/api';
import { getApiErrorText } from '~utils/getApiErrorText';
import { callErrorToast } from '~utils/toasts';

interface SendMessageToBarryResponse {
	success: boolean;
}

interface SendMessageVariables {
	message: string;
	conversationId?: string;
}

interface UseSendMessageToBarryResponse {
	data: SendMessageToBarryResponse | null;
	isFetching: boolean;
	sendMessage: (variables: SendMessageVariables) => void;
}

export function useSendMessageToBarry(): UseSendMessageToBarryResponse {
	const { data, isLoading, mutate } = useMutation<
		SendMessageToBarryResponse,
		ApiError,
		SendMessageVariables
	>({
		mutationFn: async ({ message, conversationId }) => {
			const {
				data: { success },
			} = await sendMessageToBarry({
				message,
				conversationId,
			});

			return {
				success,
			};
		},
		onError: (error) => {
			Sentry.captureException(error);
			callErrorToast({
				content: getApiErrorText(
					error,
					'An error occurred while sending your message to Barry',
				),
			});
		},
	});

	return {
		data: data ?? null,
		isFetching: isLoading,
		sendMessage: mutate,
	};
}
